/**
 * Wire schemas for the SFRA Insiders claim endpoints (COM-506).
 *
 * `GET Loyalty-FormData` and `POST Loyalty-RedeemPoints` are SFRA controllers
 * (`org_hec/cartridge/controllers/Loyalty.js`); SFRA's `res.json` merges its
 * own envelope (`action`, `queryString`, `locale`) into every reply, hence
 * `.passthrough()` everywhere. Only the keys named here are the contract.
 */
import { z } from "zod";

/**
 * The three SFRA `type` values. Closed on purpose: an unknown `type` reaches
 * the controller's default branch, which writes `JSON.stringify(undefined)` to
 * `profile.custom.insidersLoyaltyData` and WIPES the shopper's claim history.
 */
export const CLAIM_VARIANTS = [
  "monthly",
  "annualContribution",
  "futurePlans",
] as const;

export const claimVariantSchema = z.enum(CLAIM_VARIANTS);

export const isClaimVariant = (
  value: unknown,
): value is (typeof CLAIM_VARIANTS)[number] =>
  claimVariantSchema.safeParse(value).success;

/**
 * `amount` is written as the raw form string (`loyaltyHelper.js:72`), but the
 * blob is free text on the profile, so a number is tolerated and normalised.
 */
const optionalText = z
  .union([z.string(), z.number()])
  .optional()
  .transform(value => (value === undefined ? undefined : String(value)));

/** `last` is a Date serialised by `JSON.stringify` — read but never used here. */
const claimRecordSchema = z
  .object({ last: z.unknown().optional() })
  .passthrough();

/**
 * `profile.custom.insidersLoyaltyData`, as echoed by `loyaltyData` and
 * `redemptions`. Each key falls back to `{}` on a malformed value so one bad
 * record cannot blank the whole form.
 */
export const loyaltyDataSchema = z.object({
  monthly: claimRecordSchema.catch({}),
  annualContribution: claimRecordSchema
    .extend({ amount: optionalText })
    .catch({}),
  futurePlans: claimRecordSchema
    .extend({ answer: z.boolean().optional() })
    .catch({}),
});

export const formDataResponseSchema = z
  .object({
    csrf: z.string(),
    loyaltyData: loyaltyDataSchema,
    isAuthenticated: z.boolean(),
    actionUrl: z.string(),
  })
  .passthrough();

/** 200 `{ success: true, message, redemptions }`. */
export const claimSuccessSchema = z
  .object({
    success: z.literal(true),
    message: z.string(),
    redemptions: loyaltyDataSchema.optional().catch(undefined),
  })
  .passthrough();

/**
 * 200 `{ success: false, error, redemptions? }` — Annex rejected the claim
 * (already claimed, opted out `AC1009`), the shopper is not signed in, or the
 * `type` was unknown. `error` is HTML (`Resource.msgf` interpolates a link).
 */
export const claimRejectedSchema = z
  .object({
    success: z.literal(false),
    error: z.string(),
    redemptions: loyaltyDataSchema.optional().catch(undefined),
  })
  .passthrough();

/**
 * 500 `{ csrfError: true, redirectUrl }` from `CSRF-AjaxFail` after a 302.
 * `validateAjaxRequest` has also logged the SFRA session out by then.
 */
export const csrfErrorSchema = z
  .object({ csrfError: z.literal(true) })
  .passthrough();

/**
 * 500 `{ error: {}, message }` from `Error-Start` — an Annex timeout or
 * non-JSON Annex reply throws inside the helper. Only answered as JSON when
 * the request carried `X-Requested-With: XMLHttpRequest`.
 */
export const errorStartSchema = z.object({ message: z.string() }).passthrough();
